import { Field, TextInput } from "@zapier/design-system";
import type { FieldBlockFileUpload } from "../../types";
import { FieldGroup } from "components/BlockEditor/components";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { FILE_EXTENSIONS_WHITELIST } from "utils/FILE_EXTENSIONS_WHITELIST";

type Props = {
  config: FieldBlockFileUpload;
  handleChange: <T>(newConfig: Partial<T>) => void;
  handleInputChange: <T>(
    name: keyof T
  ) => (e: ChangeEvent<HTMLInputElement>) => void;
  errors: Set<string>;
  setErrors: Dispatch<SetStateAction<Set<string>>>;
};

export const allowedFileTypesConfigToArray = (fileTypes: string) => {
  return fileTypes
    .split(",")
    .map((ext) => ext.toLowerCase().split(".").pop() ?? "")
    .filter((ext) => ext !== "");
};

const getUnsupportedFileErrorMessage = (fileTypes?: string) => {
  if (!fileTypes) return;

  const unsupportedTypes: string[] = [];
  const userAllowedFileTypes = allowedFileTypesConfigToArray(fileTypes);
  userAllowedFileTypes.forEach(
    (ext) =>
      !FILE_EXTENSIONS_WHITELIST.includes(ext) && unsupportedTypes.push(ext)
  );

  return `Unsupported file types: ${unsupportedTypes.join(",")}`;
};

export default function FileUploadFields(props: Props) {
  const validateFileTypes = () => {
    if (!props.config.allowedFileTypes) return;
    const userAllowedFileTypes = allowedFileTypesConfigToArray(
      props.config.allowedFileTypes
    );

    if (
      userAllowedFileTypes.some(
        (ext) => !FILE_EXTENSIONS_WHITELIST.includes(ext)
      )
    ) {
      props.setErrors(new Set(props.errors).add("allowedFileTypes"));
    } else {
      const errors = new Set(props.errors);
      errors.delete("allowedFileTypes");
      props.setErrors(errors);
    }
  };

  return (
    <>
      <FieldGroup>
        <Field
          error={
            props.errors.has("allowedFileTypes")
              ? getUnsupportedFileErrorMessage(props.config.allowedFileTypes)
              : undefined
          }
          label="Allowed File Types"
          renderInput={(inputProps) => (
            <TextInput
              {...inputProps}
              onChange={props.handleInputChange("allowedFileTypes")}
              value={props.config.allowedFileTypes ?? ""}
              onBlur={() => validateFileTypes()}
            />
          )}
          renderHelpText={() =>
            "Separate with commas. Allowed types are .jpg, .png, .pdf, .doc, .docx ..etc"
          }
        />
      </FieldGroup>
    </>
  );
}
